import React from "react";
import TaskList from "./TaskList";
import { ChevronRight, Ellipsis, Plus } from "lucide-react";
import SmartDatePicker from "../SmartDatePicker";
import TaskPriority from "../TaskPriority";
import TaskEmployees from "../TaskEmployees";
import { useCallback, useState, useEffect, useRef } from "react";
import { getStatusColor } from "../../Pages/utils/taskUtils";
import Input from "../Input";

function StatusSection({
    status,
    tasks,
    taskOrder,
    sensors,
    handleDragEnd,
    toggleTaskSelection,
    selectedTasks,
    activeProjectId,
    activeProject,
    activeSubProjectObj,
    employees,
    currentUser,
    updateProject,
    setToastMessage,
    setToastType,
    setCurrentTask,
    setRunningTaskId,
    setEditData,
    setShowDescriptionPopup,
    setSelectedTasks,
    isBelow1280,
    collapsedStatuses,
    toggleStatus,
    statuses,
    selectedTaskObjects,
    statusRef,
    setStatuses,
    setTaskOrder,
    projects,
    activeSubProjectId,
    renameStatusMutation,
    formatStatusLabel,
    tasksByStatus,
    setOpenMenuStatus,
    openMenuStatus,
    menuRef,
    deleteStatusMutation,
    addTaskRef,
    CircleCheck,
    localProjects,
    dragDropTaskMutation,
    nowIST,
    refetch
}) {

    const initStatusObject = (value) =>
        Object.fromEntries(statuses.map((s) => [s, value]));
    const [renameInput, setRenameInput] = useState("");
    const [editingStatus, setEditingStatus] = useState(null);
    const [taskInput, setTaskInput] = useState(() => initStatusObject(""));
    const [selectedEmployees, setSelectedEmployees] = useState(() => initStatusObject([]));
    const [priority, setPriority] = useState(() => initStatusObject(""));
    const [dueDate, setDueDate] = useState(() => initStatusObject(null));
    const [isSaving, setIsSaving] = useState(false);
    const [editingStatusName, setEditingStatusName] = useState(null);
    const [showDatePicker, setShowDatePicker] = useState(null);
    const inputRef = useRef(null);

    const getTaskCount = (status) => tasksByStatus[status]?.length || 0;

    useEffect(() => {
        setTaskInput((p) => ({ ...initStatusObject(""), ...p }));
        setSelectedEmployees((p) => ({ ...initStatusObject([]), ...p }));
        setPriority((p) => ({ ...initStatusObject(""), ...p }));
        setSelectedTasks((p) => ({ ...initStatusObject([]), ...p }));
        setDueDate((p) => ({ ...initStatusObject(null), ...p }));
    }, [statuses]);

    const handleUpdateProject = useCallback(
        (status) => {
            if (!taskInput[status]?.trim()) {
                setToastMessage("Task cannot be empty");
                setToastType("error");
                return;
            }

            setIsSaving(true);

            const taskToUpdate = selectedTaskObjects?.find(
                (t) => t.status === status
            );

            const currentUserName =
                currentUser?.fullName ||
                currentUser?.firstName ||
                currentUser?.username ||
                "Unknown";

            const createdByTask = {
                userId: currentUser?.userId || "Unknown",
                fullName:
                    currentUser?.fullName ||
                    currentUser?.firstName ||
                    currentUser?.username ||
                    "Unknown",
                email: currentUser?.email || "info@hubsyntax.com",
            };

            const selectedIds = selectedEmployees[status] || [];

            const assignedEmployees = selectedIds
                .map((id) => {
                    const emp = employees.find((e) => e.employeeId === id);
                    if (!emp) return null;

                    return {
                        _id: emp.employeeId,
                        employeeId: emp.employeeId,
                        firstName: emp.firstName || "",
                        lastName: emp.lastName || "",
                        officeEmail: emp.officeEmail || "Unknown",
                        image: emp.image || "",
                        department: emp.departmentName || emp.department || "",
                    };
                })
                .filter(Boolean);

            const comments = [];

            const nowIST = new Date().toLocaleString("sv-SE", {
                timeZone: "Asia/Kolkata",
            }).replace(" ", "T");

            if (!taskToUpdate) {
                comments.push({
                    text: `${currentUserName} created this task`,
                    createdBy: currentUserName,
                    createdAt: nowIST,
                });

                comments.push({
                    text: `${currentUserName} set status to ${status}`,
                    createdBy: currentUserName,
                    createdAt: nowIST,
                });
            } else if (taskToUpdate.status !== status) {
                comments.push({
                    text: `${currentUserName} changed status from ${taskToUpdate.status} to ${status}`,
                    createdBy: currentUserName,
                    createdAt: nowIST,
                });
            }

            if (assignedEmployees.length > 0) {
                const assignedNames = assignedEmployees.map((u) => u.firstName).join(", ");

                comments.push({
                    text: `${currentUserName} assigned to: ${assignedNames}`,
                    createdBy: currentUserName,
                    createdAt: nowIST,
                });
            }

            if (dueDate[status]) {
                comments.push({
                    text: `${currentUserName} set the due date to ${new Date(
                        dueDate[status]
                    ).toLocaleDateString()}`,
                    createdBy: currentUserName,
                    createdAt: nowIST,
                });
            }

            if (priority[status]) {
                comments.push({
                    text: `${currentUserName} set priority to ${priority[status]}`,
                    createdBy: currentUserName,
                    createdAt: nowIST,
                });
            }

            const formatMySQLDate = (date) => {
                const d = new Date(date);

                const year = d.getFullYear();
                const month = String(d.getMonth() + 1).padStart(2, "0");
                const day = String(d.getDate()).padStart(2, "0");

                const hours = String(d.getHours()).padStart(2, "0");
                const minutes = String(d.getMinutes()).padStart(2, "0");
                const seconds = "00";

                return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
            };

            const payload = {
                id: activeProjectId,
                taskId: taskToUpdate?._id || null,
                subProjectId: activeSubProjectObj?._id || null,
                taskTitle: taskInput[status],
                status,
                assignedEmployees,
                priority: priority[status],
                dueDate: dueDate[status]
                    ? formatMySQLDate(dueDate[status])
                    : null,
                comments,
                createdByTask,
                createdBy: currentUserName,
            };
            updateProject.mutate(payload, {
                onSuccess: async () => {
                    try {
                        await refetch();

                        setTaskInput((prev) => ({ ...prev, [status]: "" }));
                        setSelectedEmployees((prev) => ({ ...prev, [status]: [] }));
                        setPriority((prev) => ({ ...prev, [status]: "" }));
                        setDueDate((prev) => ({ ...prev, [status]: null }));
                        setEditingStatus(null);

                        setToastMessage(
                            taskToUpdate
                                ? "Task updated successfully!"
                                : "Task added successfully!"
                        );
                        setToastType("success");

                    } catch (err) {
                        console.error("Refetch error:", err);
                    } finally {
                        setIsSaving(false);
                    }
                },

                onError: (err) => {
                    setToastMessage("Error updating task: " + err.message);
                    setToastType("error");
                    setIsSaving(false);
                },
            });
        },
        [
            taskInput,
            selectedTaskObjects,
            selectedEmployees,
            employees,
            currentUser,
            dueDate,
            priority,
            activeProjectId,
            activeSubProjectObj,
            updateProject,
        ]
    );

    const handleRenameStatus = (oldStatusLocal) => {

        const normalize = (v) =>
            v.trim().toLowerCase().replace(/\s+/g, "_");

        const newStatus = normalize(renameInput);

        if (!newStatus || statuses.includes(newStatus)) {
            setEditingStatusName(null);
            return;
        }

        setStatuses((prev) =>
            prev.map((s) => (s === oldStatusLocal ? newStatus : s))
        );

        setTaskOrder((prev) => {
            const updated = { ...prev };

            if (updated[oldStatusLocal]) {
                updated[newStatus] = updated[oldStatusLocal];
                delete updated[oldStatusLocal];
            }

            return updated;
        });

        const activeProject = projects.find((p) => p.id === activeProjectId);

        if (!activeProject) return;

        if (activeProject.isMultiProject && !activeSubProjectId) return;

        renameStatusMutation.mutate({
            id: activeProjectId,
            subProjectId: activeProject.isMultiProject
                ? activeSubProjectId
                : null,
            oldStatus: oldStatusLocal,
            newStatus
        });

        setEditingStatusName(null);
    };

    const dhandle = (statusToDelete) => {
        if (!activeProjectId) return;

        if (!window.confirm(`Delete status "${statusToDelete}" and all its tasks?`)) {
            return;
        }

        deleteStatusMutation.mutate(
            {
                projectId: activeProjectId,
                status: statusToDelete,
                subProjectId: activeProject?.isMultiProject
                    ? activeSubProjectId
                    : null,
            },
            {
                onSuccess: () => {
                    setOpenMenuStatus(null);

                },
                onError: (err) => {
                    console.error(err);
                    setToastMessage(err.message || "Failed to remove status");
                    setToastType("error");
                },
            }
        );
    };

    useEffect(() => {
        if (editingStatus === status && inputRef.current) {
            inputRef.current.focus();
        }
    }, [editingStatus, status]);

    return (
        <div className="mb-8">
            <div>
                {editingStatusName === status ? (
                    <div className="inline-flex items-center gap-2" ref={statusRef}>
                        <input
                            type="text"
                            value={renameInput || ''}
                            onChange={(e) => setRenameInput(e.target.value.toLowerCase())}
                            className="px-2 py-1 border rounded text-sm outline-none"
                            onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                    handleRenameStatus(status);
                                }
                                if (e.key === "Escape") {
                                    setEditingStatusName(null);
                                }
                            }}
                            onBlur={() => handleRenameStatus(status)}
                            autoFocus
                        />
                    </div>
                ) : (
                    <div className="flex items-center gap-2">
                        <ChevronRight
                            size={18}
                            color="grey"
                            className={`cursor-pointer transition-transform duration-200 ${collapsedStatuses[status] ? "" : "rotate-90"
                                }`}
                            onClick={() => toggleStatus(status)}
                        />
                        <h2
                            onDoubleClick={() => {
                                setEditingStatusName(status);
                                setRenameInput(formatStatusLabel(status));
                            }}
                            className={`cursor-pointer text-[12px] sm:text-[14px] font-normal inline-block  px-2 py-1 rounded ${getStatusColor(status)}`}
                        >
                            {formatStatusLabel(status)}
                        </h2>
                        <span className="ml-2 px-1.5 py-0.5 text-xs rounded-full bg-gray-200 text-gray-700">
                            {getTaskCount(status)}
                        </span>
                        <div className="relative">
                            <Ellipsis
                                size={18}
                                color="grey"
                                className="cursor-pointer"
                                onClick={() =>
                                    setOpenMenuStatus(openMenuStatus === status ? null : status)
                                }
                            />
                            {openMenuStatus === status && (
                                <div ref={menuRef} className="absolute mt-2 flex flex-wrap gap-[2px] p-[5px] left-0 bg-white shadow-lg rounded  z-50 w-40">
                                    <button
                                        className=" block w-full text-left text-[12px] sm:text-[14px] px-3 py-2 hover:bg-gray-100 "
                                        onClick={() => {
                                            setEditingStatusName(status);
                                            setRenameInput(status);
                                            setOpenMenuStatus(null);
                                        }}
                                    >
                                        Rename Status
                                    </button>
                                    <button
                                        className=" block w-full text-left text-[12px] sm:text-[14px] px-3 py-2 hover:bg-gray-100 "
                                        onClick={() => dhandle(status)}
                                    >
                                        Remove Status
                                    </button>
                                </div>
                            )}
                        </div>

                    </div>
                )}
            </div>
            {!collapsedStatuses[status] && (
                <TaskList
                    status={status}
                    tasks={tasks}
                    taskOrder={taskOrder}
                    sensors={sensors}
                    handleDragEnd={handleDragEnd}
                    toggleTaskSelection={toggleTaskSelection}
                    selectedTasks={selectedTasks}
                    activeProjectId={activeProjectId}
                    activeProject={activeProject}
                    activeSubProjectObj={activeSubProjectObj}
                    employees={employees}
                    currentUser={currentUser}
                    updateProject={updateProject}
                    setToastMessage={setToastMessage}
                    setToastType={setToastType}
                    setCurrentTask={setCurrentTask}
                    setRunningTaskId={setRunningTaskId}
                    setEditData={setEditData}
                    setShowDescriptionPopup={setShowDescriptionPopup}
                    setSelectedTasks={setSelectedTasks}
                    isBelow1280={isBelow1280}
                    setTaskOrder={setTaskOrder}
                    localProjects={localProjects}
                    dragDropTaskMutation={dragDropTaskMutation}
                    nowIST={nowIST}
                />
            )}

            {!collapsedStatuses[status] && (editingStatus !== status ? (
                <div
                    className="flex text-[12px] sm:text-[14px] items-center gap-1 mt-[10px] cursor-pointer  text-gray-500 border-b-0 hover:border-b hover:border-gray-300"
                    onClick={() => setEditingStatus(status)}
                >
                    <Plus size={14} />
                    <span>Add Task</span>
                </div>
            ) : (
                <div
                    ref={addTaskRef}
                    className="flex items-center gap-2 p:1 sm:p-2 bg-white mt-[10px] rounded-md shadow-sm hover:bg-gray-50 cursor-pointer mb-2"
                >
                    <div className="flex items-center justify-center rounded-full bg-[#299764] text-white">
                        <CircleCheck size={16} />
                    </div>
                    <div className="w-[50%]">
                        <Input
                            ref={inputRef}
                            type="text"
                            className="flex w-[50%] px-2 py-1 outline-none text-[14px]"
                            placeholder="Task Name or type for commands"
                            value={taskInput[status] || ""}
                            onChange={(e) =>
                                setTaskInput((prev) => ({ ...prev, [status]: e.target.value }))
                            }
                        />
                    </div>
                    <div className="hidden xs:inline-flex smart-date-task ">
                        <SmartDatePicker
                            open={showDatePicker === status}
                            setOpen={(v) => setShowDatePicker(v ? status : null)}
                            selected={dueDate[status]}
                            setSelected={(d) => {
                                setDueDate((prev) => ({
                                    ...prev,
                                    [status]: d,
                                }));

                            }}
                            placement={isBelow1280 ? "top" : "bottom"}
                            align={isBelow1280 ? "right" : "left"}
                        />
                    </div>

                    <div className="relative hidden zx:inline-flex  ">
                        <TaskPriority
                            value={priority[status]}
                            onChange={(level) =>
                                setPriority((prev) => ({ ...prev, [status]: level }))
                            }
                        />
                    </div>
                    <div className="taskEmployees-container hidden zx:flex">
                        <TaskEmployees
                            selected={selectedEmployees[status]}
                            onChange={(arr) =>
                                setSelectedEmployees((prev) => ({ ...prev, [status]: arr }))
                            }
                            employees={employees}
                            className="custom-dropdown"
                            placement={isBelow1280 ? "top" : "bottom"}
                            align={isBelow1280 ? "right" : "left"}
                        />
                    </div>
                    <button
                        className="px-3 py-1 bg-gray-200 rounded hover:bg-gray-300  text-[11px] sm:text-[15px]"
                        onClick={() => setEditingStatus(null)}
                    >
                        Cancel
                    </button>
                    <button
                        className={`px-3 py-1 rounded  text-[11px] sm:text-[15px] text-white 
                ${isSaving
                                ? "bg-gray-400 cursor-not-allowed"
                                : "bg-purple-600 hover:bg-purple-700"
                            }`}
                        disabled={isSaving}
                        onClick={() => handleUpdateProject(status)}
                    >
                        {isSaving ? "Saving..." : "Save"}
                    </button>
                </div>
            ))}
        </div>
    );
}

export default React.memo(StatusSection);